import { DateTime, Duration } from 'luxon';

import { APIConfig } from 'src/config';
import {
  P1D, P1M, PT30M, TIME_ZONE_SYSTEM,
} from 'src/util/constants';

/**
 * Rebuilds midnight of the given date's calendar day (year/month/day) in the
 * target timezone. This is a **day-based**, not instant-preserving,
 * conversion: only `date.year`/`date.month`/`date.day` are read — the
 * date's own zone, time-of-day, and offset are discarded entirely.
 *
 * Callers are responsible for ensuring `date`'s calendar day already
 * represents the intended day (e.g. the day the user clicked in the
 * browser's local zone). Do not pass a `DateTime` produced by
 * re-interpreting an instant in another zone (e.g.
 * `DateTime.fromJSDate(jsDate, { zone: tz })`), as that can shift the
 * calendar day before it reaches this function — see
 * docs/solutions/chart-rendering/datepicker-opens-wrong-month-cross-month-range.md.
 * @param {(DateTime|null)} date - a DateTime whose year/month/day represent
 * the intended calendar day; its zone and time-of-day are ignored.
 * @param {string} tz name of timezone
 * @returns {(DateTime|null)} midnight of that calendar day in `tz`.
 */
export const dateToTimeInTimezone = (date, tz) => {
  if (!date) { return null; }

  const timezone = tz || APIConfig().DEFAULT_TIMEZONE;

  return DateTime.fromObject(
    { year: date.year, month: date.month, day: date.day },
    { zone: timezone },
  );
};

/**
 * For a given date range a finish date is represented as midnight of the day in our date range
 * object, however as a time range it is considered at 24:00:00 that is midnight the following day.
 * A day is a 24 hours interval (not withstanding daylight saving and/or leap seconds). Hence the
 * addition of one day.
 * @param {object} dateRange
 * @param {DateTime} dateRange.start
 * @param {DateTime} dateRange.finish
 * @param {string} tz
 * @returns {object} - time range.
 */
export const dateRangeToTimeRange = (dateRange, tz = null) => {
  if (!dateRange) return null;

  const timezone = tz || APIConfig().DEFAULT_TIMEZONE;
  const { start: startDate, finish: finishDate } = dateRange;

  const start = dateToTimeInTimezone(startDate, timezone);
  const finish = dateToTimeInTimezone(finishDate, timezone)?.plus({ days: 1 });

  return { start, finish };
};

/**
 * Formats a time range into a date range for query params in the given timezone.
 * The finish value in a time range is exclusive, so we convert it to an inclusive
 * date by subtracting one day after adjusting it to the requested timezone.
 * @param {object} timespan
 * @param {DateTime} [timespan.start]
 * @param {DateTime} [timespan.finish]
 * @param {string} timezone
 * @returns {{start: string|undefined, finish: string|undefined}} formatted date range.
 */
export const timeRangeToDateRange = (timespan, timezone) => {
  const { start: startTime, finish: finishTime } = timespan || {};

  const start = startTime
    ? (timezone ? startTime.setZone(timezone) : startTime).toISODate()
    : undefined;
  const finish = finishTime
    ? (timezone ? finishTime.setZone(timezone) : finishTime)
      .minus({ days: 1 })
      .toISODate()
    : undefined;

  return { start, finish };
};

/**
 * Returns the aggregation for the chart in dashboard (property & portfolio)
 * @param {object} start
 * @param {object} finish
 * Aggregation is based on calendar days so daylight-saving transitions do not
 * make a seven-day range appear longer or shorter than seven days.
 * @returns {P1D | PT30M} - aggregation
 */
export const getAggregation = (start, finish) => {
  let agg = PT30M;
  if (!start || !finish) {
    return agg;
  }
  const calendarDays = finish.diff(start, 'days').days;
  if (calendarDays > 7) {
    agg = P1D;
  }
  return agg;
};

/**
 * Returns the time span for the dashboard (property & portfolio).
 * Note the asymmetric contract: `start` is taken as-is (inclusive in, inclusive
 * out), but `finish` is taken as an EXCLUSIVE boundary (callers pass finish + 1
 * day) and converted back to an INCLUSIVE `f` internally (`ts.minus(P1D)`).
 * Use dateRangeToTimeRange or getRouteTimeRange if you need an exclusive
 * boundary for queries or aggregation.
 * @param {number} start - inclusive start unix timestamp (seconds)
 * @param {number} finish - EXCLUSIVE finish unix timestamp (seconds), i.e.
 * the inclusive finish day plus one day
 * @param {string} timezone
 * @returns {{ s: DateTime, f: DateTime }} - timespan with inclusive start (s)
 * and inclusive finish (f)
 */
export const getDashboardTimeScope = (start, finish, timezone) => {
  // Anchor to today, mirroring the route defaults so a directly-called scope
  // matches the URL-derived range. The inclusive finish defaults to yesterday
  // (the route's exclusive `today` boundary minus one day) and the start to one
  // month earlier. Deriving the start from the finish (rather than re-adding a
  // month to a shifted start) keeps the month-long window consistent regardless
  // of calendar month lengths — on 31 Mar this yields 28 Feb – 30 Mar instead
  // of 27 Feb – 27 Mar.
  let f = DateTime.local({ zone: timezone }).startOf('day').minus(Duration.fromISO(P1D));
  if (finish && timezone) {
    const ts = DateTime.fromSeconds(finish, { zone: timezone });
    if (ts.invalidExplanation === null) {
      f = ts.minus(Duration.fromISO(P1D));
    }
  }

  let s = f.minus(Duration.fromISO(P1M));
  if (start && timezone) {
    const ts = DateTime.fromSeconds(start, { zone: timezone });
    if (ts.invalidExplanation === null) {
      s = ts;
    }
  }

  return { s, f };
};

/**
 * Computes the dashboard route time range from optional ISO date query params.
 * Defaults to the month-long window ending on the effective finish: the finish
 * is today (as an exclusive next-day boundary) unless overridden, and the start
 * is one day and one month before the finish. Deriving the default start from
 * the finish keeps the window a month long even when only a finish is supplied
 * (avoiding an inverted s > f range) and avoids month-length clamping — on
 * 31 Mar the default resolves to 28 Feb – 31 Mar rather than 27 Feb – 27 Mar.
 * @param {string} [start] inclusive start day as an ISO date string
 * @param {string} [finish] inclusive finish day as an ISO date string
 * @param {string} timezone
 * @returns {{ s: DateTime, f: DateTime }} start (inclusive) and finish (exclusive).
 */
export const getRouteTimeRange = (start, finish, timezone) => {
  let f = DateTime.local({ zone: timezone }).startOf('day');
  if (finish) {
    const ts = DateTime.fromISO(finish, { zone: timezone });
    if (ts.invalidExplanation === null) {
      f = ts.plus(Duration.fromISO(P1D)).startOf('day');
    }
  }

  let s = f.minus(Duration.fromISO(P1D)).minus(Duration.fromISO(P1M));
  if (start) {
    const ts = DateTime.fromISO(start, { zone: timezone });
    if (ts.invalidExplanation === null) {
      s = ts.startOf('day');
    }
  }

  return { s, f };
};

/**
 * Returns the timezone to use, based on following priority order:
 * 1. Explicit from the URL
 * 2. Implicit from the property (if applicable)
 * 3. Implicit from config
 * 4. Implicit from system
 * @param {string} timezone
 * @returns {string} The timezone to use.
 */
export const getTimezone = (timezone = '') => {
  const searchParams = new URLSearchParams(window.location.search);
  const requestedTimezone = searchParams.get('tz');
  if (requestedTimezone) {
    return requestedTimezone;
  }

  return timezone || APIConfig().DEFAULT_TIMEZONE || TIME_ZONE_SYSTEM;
};

/**
 * Check whether the timespan has changed.
 * Compares between the proposed timespan and the current timespan.
 * If the dates are the same, logs a message and returns `false`; otherwise, returns `true`.
 * @param {DateTime} inputStart - The proposed start time of the timespan.
 * @param {DateTime} inputFinish - The proposed finish time of the timespan.
 * @param {object} currentTimespan - The current timespan to compare against.
 * @returns {boolean} - returns true if the timespan is valid and false if not.
 */
export const timespanChanged = (inputStart, inputFinish, currentTimespan) => {
  // Validate inputs
  if (!inputStart || !inputFinish || !inputStart.isValid || !inputFinish.isValid) {
    console.error('Invalid date inputs to handleTimespanUpdate:', { inputStart, inputFinish });
    return false;
  }

  // Check if this is the same as the current datespan to avoid unnecessary updates
  const isSameStart = inputStart.toMillis() === currentTimespan.start.toMillis();
  const isSameFinish = inputFinish.toMillis() === currentTimespan.finish.toMillis();

  if (isSameStart && isSameFinish) {
    console.log('Timespan update skipped - dates are identical');
    return false;
  }
  return true;
};

/**
 * Sets the timezone and start of day for the given timespan.
 * @param {DateTime} time - The time to modify.
 * @param {string} timezone - The timezone to use.
 * @returns {DateTime} - modified timespan.
 */
export const timeInZoneAtStartOfDay = (time, timezone) => {
  if (!time || !timezone) {
    return time;
  }
  return time.setZone(timezone).startOf('day');
};

/**
 * Converts a variety of time representations to epoch milliseconds.
 * @param {number|Date|{toMillis: function(): number}|null|undefined} v - The value to convert.
 * @returns {number|null} Epoch milliseconds or null if invalid.
 */
export const toMillis = (v) => {
  if (v == null) return null;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (v instanceof Date) return v.getTime();
  if (typeof v === 'object' && v !== null && 'toMillis' in v && typeof v.toMillis === 'function') {
    const n = v.toMillis();
    return Number.isFinite(n) ? n : null;
  }
  return null;
};
